import fs from "fs";
import path from "path";
import kleur from "kleur";
import { summarizeRepo } from "../../tools/repo";
import { makeProvider } from "../../agent/simpleAgent";

// Analyze project structure and generate smart Agent.md
async function analyzeProjectAndGenerateAgentMd(projectPath: string): Promise<string> {
  console.log(kleur.blue("🔍 Analyzing project structure..."));
  
  try {
    const provider = makeProvider();
    const repoSummary = summarizeRepo(120); // Get detailed repo summary
    
    const analysisPrompt = `Analyze this project and generate a comprehensive Agent.md file with project-specific instructions for an AI coding assistant.

PROJECT ANALYSIS:
${repoSummary}

Based on the project structure, files, and patterns you can see, create an Agent.md that includes:

1. **Project Context**: Describe what this project appears to be and its purpose
2. **Tech Stack**: Identify the main technologies, frameworks, and tools
3. **Architecture**: Describe the project structure and organization patterns
4. **Coding Standards**: Infer coding conventions from existing code
5. **Key Components**: Highlight important files, directories, and their purposes
6. **Development Workflow**: Identify build scripts, test setup, and development commands
7. **Specific Guidelines**: Project-specific best practices and constraints

Generate a professional, detailed Agent.md file that will help an AI assistant understand this specific project and provide better, more contextual assistance.

Format as a proper markdown file with clear sections and actionable guidance.`;

    const agentMdContent = await provider.send([
      { role: "system", content: "You are an expert software architect analyzing projects to create comprehensive development guidelines." },
      { role: "user", content: analysisPrompt }
    ]);

    return agentMdContent;
  } catch (error) {
    console.log(kleur.yellow("⚠️  Failed to analyze project with AI, using fallback template"));
    return generateFallbackAgentMd();
  }
}

function generateFallbackAgentMd(): string {
  return `# Agent Instructions

Welcome to your Metis Code project! This file contains special instructions that will be provided to the AI agent to customize its behavior for your specific project.

## Project Context

This project appears to be a software development project. The AI assistant will help you implement features, fix bugs, and maintain code quality.

## Coding Standards & Preferences

### Code Style
- Follow consistent naming conventions
- Use proper indentation and formatting
- Include meaningful comments and documentation
- Prefer readable, maintainable code over clever solutions

### Testing
- Write tests for new functionality
- Maintain good test coverage
- Test edge cases and error conditions

## Best Practices

### DO:
- ✅ Add proper error handling
- ✅ Write clear, self-documenting code
- ✅ Include input validation
- ✅ Follow the existing project patterns
- ✅ Consider security implications

### DON'T:
- ❌ Skip input validation
- ❌ Commit sensitive data
- ❌ Write overly complex functions
- ❌ Ignore existing code conventions

---

*Generated by Metis Code on ${new Date().toISOString().split('T')[0]}*

<!-- Edit this file to add project-specific instructions and guidelines -->
`;
}

export async function runInit(args: string[]) {
  const cwd = process.cwd();
  const metisDir = path.join(cwd, ".metis");
  const sessionsDir = path.join(metisDir, "sessions");
  const secretsFile = path.join(metisDir, "secrets.json");
  const cfgPath = path.join(cwd, "metis.config.json");
  const agentMdPath = path.join(cwd, "Agent.md");

  if (!fs.existsSync(metisDir)) fs.mkdirSync(metisDir);
  if (!fs.existsSync(sessionsDir)) fs.mkdirSync(sessionsDir);

  if (!fs.existsSync(cfgPath)) {
    fs.writeFileSync(
      cfgPath,
      JSON.stringify(
        {
          provider: "openai",
          model: "gpt-4o",
          temperature: 0.2,
          safety: { dryRun: true, requireExecApproval: true },
          ignore: ["node_modules/**", ".git/**", "dist/**", ".metis/sessions/**"],
        },
        null,
        2
      ) + "\n"
    );
    console.log(`Created config: ${path.relative(cwd, cfgPath)}`);
  } else {
    console.log(`Config exists: ${path.relative(cwd, cfgPath)}`);
  }

  if (!fs.existsSync(secretsFile)) {
    fs.writeFileSync(secretsFile, JSON.stringify({}, null, 2) + "\n");
    console.log(`Created secrets store: ${path.relative(cwd, secretsFile)}`);
  } else {
    console.log(`Secrets store exists: ${path.relative(cwd, secretsFile)}`);
  }

  // Always create Agent.md with intelligent project analysis
  if (!fs.existsSync(agentMdPath)) {
    console.log();
    console.log(kleur.cyan("🤖 Generating intelligent Agent.md based on your project..."));
    
    try {
      const agentMdContent = await analyzeProjectAndGenerateAgentMd(cwd);
      fs.writeFileSync(agentMdPath, agentMdContent);
      console.log(kleur.green("✅ ") + `Created ${kleur.yellow("Agent.md")} with project-specific instructions`);
      console.log(kleur.gray("   The AI analyzed your project structure and generated tailored guidelines"));
    } catch (error) {
      console.log(kleur.red("❌ ") + "Failed to generate Agent.md");
      console.log(kleur.gray(`   Error: ${error.message}`));
    }
  } else {
    console.log(kleur.yellow("⚠️  ") + `Agent.md already exists: ${path.relative(cwd, agentMdPath)}`);
    console.log(kleur.gray("   Delete it and run init again to regenerate with project analysis"));
  }

  console.log();
  console.log(kleur.green("🎉 Metis workspace initialized!"));
  
  console.log();
  console.log(kleur.white("Next steps:"));
  console.log(kleur.gray("1. ") + `Review and customize ${kleur.yellow("Agent.md")} (AI-generated project guidelines)`);
  console.log(kleur.gray("2. ") + `Configure your API key: ${kleur.cyan("metiscode auth set openai YOUR_API_KEY")}`);
  console.log(kleur.gray("3. ") + `Start coding: ${kleur.cyan('metiscode run "your task"')}`);
  
  console.log();
  console.log(kleur.blue("💡 Pro tip: ") + `The Agent.md file contains AI-generated guidelines specific to your project`);
  console.log(kleur.gray("   Edit it anytime to refine how the AI assistant works with your codebase"));
  console.log(kleur.gray("\n🔒 Keep .metis/secrets.json out of git!"));
}

